export type PurchaseLine = {
	description: string;
	amount: string;
	qty?: string;
	unitPrice?: string;
	category?: string;
};

export type PurchaseLineDraft = {
	key: string;
	description: string;
	qty: string;
	unitPrice: string;
};

const MONEY = /^\d+(\.\d{1,2})?$/;

export function parsePurchaseMoney(value: string): number {
	const trimmed = value.trim().replace(/,/g, "");
	if (!trimmed) return 0;
	const parsed = Number(trimmed);
	return Number.isFinite(parsed) ? parsed : 0;
}

export function formatPurchaseMoney(value: number): string {
	const rounded = Math.round(value * 100) / 100;
	return rounded.toFixed(2);
}

export function parsePurchaseQty(value: string | undefined): number {
	const trimmed = (value ?? "").trim();
	if (!trimmed) return 1;
	const parsed = Number(trimmed);
	if (!Number.isFinite(parsed) || parsed <= 0) return 1;
	return Math.floor(parsed);
}

export function normalizePurchaseLine(line: PurchaseLine): PurchaseLine {
	const description = line.description.trim();
	const qty = parsePurchaseQty(line.qty);
	const hasUnit = line.unitPrice != null && line.unitPrice.trim() !== "";
	const unitPrice = hasUnit ? parsePurchaseMoney(line.unitPrice ?? "") : parsePurchaseMoney(line.amount);
	const amount = formatPurchaseMoney(qty * unitPrice);
	return {
		...line,
		description,
		qty: String(qty),
		unitPrice: formatPurchaseMoney(unitPrice),
		amount,
		category: line.category ?? "other",
	};
}

export function normalizePurchaseLines(lines: PurchaseLine[]): PurchaseLine[] {
	return lines
		.map(normalizePurchaseLine)
		.filter((line) => line.description.length > 0);
}

export function lineRowTotal(line: PurchaseLine): number {
	return parsePurchaseMoney(normalizePurchaseLine(line).amount);
}

export function docTotalFromLines(lines: PurchaseLine[] | undefined): number {
	const rows = lines?.length ? lines : [{ description: "", amount: "0" }];
	return normalizePurchaseLines(rows).reduce((sum, line) => sum + lineRowTotal(line), 0);
}

export function lineQuantity(line: PurchaseLine): number {
	return parsePurchaseQty(normalizePurchaseLine(line).qty);
}

export function lineUnitPrice(line: PurchaseLine): number {
	return parsePurchaseMoney(normalizePurchaseLine(line).unitPrice ?? line.amount);
}

export function purchaseLinesFromDoc(lines: PurchaseLine[] | undefined): PurchaseLine[] {
	if (!lines?.length) return [{ description: "", amount: "0", qty: "1", unitPrice: "0" }];
	return lines.map((row) => normalizePurchaseLine(row));
}

export function lineDraftsFromDoc(lines: PurchaseLine[] | undefined): PurchaseLineDraft[] {
	const rows = purchaseLinesFromDoc(lines);
	return rows.map((row, index) => ({
		key: `line-${index}`,
		description: row.description,
		qty: row.qty ?? "1",
		unitPrice: row.unitPrice ?? row.amount,
	}));
}

export function emptyLineDraft(key = "line-0"): PurchaseLineDraft {
	return { key, description: "", qty: "1", unitPrice: "" };
}

export function linesFromDrafts(drafts: PurchaseLineDraft[]): PurchaseLine[] {
	return normalizePurchaseLines(drafts.map((row) => ({
		description: row.description,
		qty: row.qty,
		unitPrice: row.unitPrice,
		amount: "0",
		category: "other",
	})));
}

export function draftSubtotal(drafts: PurchaseLineDraft[]): number {
	return docTotalFromLines(linesFromDrafts(drafts));
}

export function purchaseLinesFromSingleLineForm(fields: FormData): PurchaseLine[] {
	const description = String(fields.get("line") ?? "").trim();
	const qty = String(fields.get("line_qty") ?? "1");
	const unitPrice = String(fields.get("amount") ?? "").trim();
	if (!description) return [];
	return normalizePurchaseLines([{ description, qty, unitPrice, amount: "0", category: "other" }]);
}

export function validateSalesOrderInput(input: {
	party: string;
	refNo: string;
	docAt: string;
	lines: PurchaseLine[];
}): string | null {
	if (!input.party.trim()) return "Enter a customer.";
	if (!input.refNo.trim()) return "Enter a sales order number.";
	if (!/^\d{4}-\d{2}-\d{2}$/.test(input.docAt.trim())) return "Enter a valid order date.";
	const lines = normalizePurchaseLines(input.lines);
	if (lines.length === 0) return "Add at least one line item.";
	for (const [index, line] of lines.entries()) {
		if (!line.description.trim()) return `Line ${index + 1} needs a description.`;
		const qty = parsePurchaseQty(line.qty);
		if (qty < 1) return `Line ${index + 1} quantity must be at least 1.`;
		const unit = parsePurchaseMoney(line.unitPrice ?? line.amount);
		if (unit < 0) return `Line ${index + 1} unit price cannot be negative.`;
		if (!MONEY.test(formatPurchaseMoney(unit))) return `Line ${index + 1} unit price is invalid.`;
	}
	if (lines.length > 50) return "A sales order can have at most 50 lines.";
	return null;
}

export function purchaseStatusAfterSend(listId: string, currentStatus: string): string | null {
	if (currentStatus !== "draft") return null;
	if (listId === "sales-orders") return "confirmed";
	if (listId === "quotations" || listId === "invoices" || listId === "purchase-orders") return "sent";
	return null;
}

export function purchaseStatusOptionsIncludes(listId: string, status: string): boolean {
	if (listId === "sales-orders") return ["draft", "confirmed", "fulfilled"].includes(status);
	if (listId === "quotations") return ["draft", "sent", "accepted", "declined"].includes(status);
	return true;
}
